import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { userApi } from '../../api/user.api';
import { useAuth } from '../../store/AuthContext';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Spinner } from '../../components/ui/index';
import { normalizeMenu, fa, getIcon, Icon } from '../../components/navigation/Sidebar';
import { invalidateMenuPreferencesCache } from '../../utils/menuPreferences';
import { fetchRoleMenuItems } from '../../utils/menuAccess';

// Lets the logged-in account reorder/hide their own sidebar menu (see
// sql/ui_preferences.sql: ui_prefrence + menu_order). Only the top-level
// items are reorderable - main_menu_index is a flat index into that array,
// a group like "Lists"/"Reports" moves as a single block, its own children
// aren't individually reordered.
export default function MenuOrderPage() {
  const { user } = useAuth();
  const { addToast } = useUI();
  const [rawItems, setRawItems] = useState([]); // normalized menu, original (main_menu_index) order
  const [rows, setRows] = useState([]); // [{mainIndex, item, hide}] - display/drag order
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dragIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const toPath = useCallback((key) => {
    const k = key?.toLowerCase().replace(/\s+/g, '_');
    const PATH_MAP = {
      home: '/home', profile_page: '/dashboard', edit_my_info: '/profile/edit',
      ticket_form: '/tickets/new', simple_user_unseen_ticket: '/dashboard', setting: '/settings',
    };
    return PATH_MAP[k] || `/${k}`;
  }, []);

  useEffect(() => {
    Promise.all([
      fetchRoleMenuItems(user?.type, toPath, normalizeMenu),
      userApi.getMenuPreferences().catch((err) => {
        if (err?.response?.status === 501) {
          addToast('جدول ترتیب منو روی سرور ساخته نشده — به مدیر سیستم اطلاع دهید', 'error');
        }
        return { data: { items: [] } };
      }),
    ]).then(([menuResult, prefsRes]) => {
      const items = menuResult.items;
      if (menuResult.fromFallback) {
        addToast('منوی پیش‌فرض بارگذاری شد — ترتیب منو ممکن است با سرور هم‌خوان نباشد', 'warning');
      }
      setRawItems(items);

      const prefByIndex = new Map((prefsRes.data?.items || []).map((p) => [p.main_menu_index, p]));
      const withMeta = items.map((item, i) => {
        const pref = prefByIndex.get(i);
        return { mainIndex: i, item, hide: pref?.hide || false, order: pref ? pref.new_menu_index : i };
      });
      withMeta.sort((a, b) => a.order - b.order);
      setRows(withMeta.map(({ mainIndex, item, hide }) => ({ mainIndex, item, hide })));
    }).catch(() => addToast('خطا در بارگذاری منو', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.type]);

  const toggleHide = (i) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, hide: !r.hide } : r)));

  const moveRow = (from, to) => {
    if (from === to || from == null || to == null) return;
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const items = rows.map((r, newIndex) => ({ main_menu_index: r.mainIndex, new_menu_index: newIndex, hide: r.hide }));
      await userApi.saveMenuPreferences({ items });
      invalidateMenuPreferencesCache();
      addToast('ترتیب منو ذخیره شد', 'success');
    } catch {
      addToast('خطا در ذخیره ترتیب منو — احتمالاً جدول ui_prefrence روی سرور ساخته نشده است', 'error');
    }
    setSaving(false);
  };

  const reset = () => {
    setRows(rawItems.map((item, i) => ({ mainIndex: i, item, hide: false })));
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>;

  return (
    <div className="fade-in">
      <PageHeader
        title="ترتیب منو"
        subtitle="آیتم‌های منو را جابه‌جا کنید یا مخفی کنید"
        action={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Link to="/settings" style={{ fontSize: 13, color: '#64748b', textDecoration: 'none', marginLeft: 8 }}>← تنظیمات</Link>
            <Btn variant="secondary" onClick={reset}>بازنشانی</Btn>
            <Btn onClick={save} loading={saving}>ذخیره</Btn>
          </div>
        }
      />

      <div style={{ maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((row, i) => {
          const label = row.item.label || '';
          const isDragOver = dragOverIndex === i;
          return (
            <div
              key={row.mainIndex}
              draggable
              onDragStart={() => { dragIndex.current = i; }}
              onDragEnter={(e) => { e.preventDefault(); setDragOverIndex(i); }}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={() => setDragOverIndex((cur) => (cur === i ? null : cur))}
              onDrop={(e) => {
                e.preventDefault();
                moveRow(dragIndex.current, i);
                dragIndex.current = null;
                setDragOverIndex(null);
              }}
              onDragEnd={() => { dragIndex.current = null; setDragOverIndex(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#fff', border: `1.5px solid ${isDragOver ? '#29AAE1' : '#e2e8f0'}`,
                borderRadius: 10, padding: '10px 12px', opacity: row.hide ? 0.5 : 1,
                transition: 'border-color 0.15s, opacity 0.15s', cursor: 'grab',
              }}
            >
              <span style={{ color: '#94a3b8', fontSize: 16, cursor: 'grab', flexShrink: 0 }} title="جابه‌جا کردن">⠿</span>
              <span style={{ width: 30, height: 30, borderRadius: 8, background: '#EDF3FC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#29AAE1' }}>
                <Icon name={getIcon(label)} size={14} />
              </span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{fa(label)}</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b', cursor: 'pointer' }}>
                <input type="checkbox" checked={row.hide} onChange={() => toggleHide(i)} />
                مخفی
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
